import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { X, Bell } from 'lucide-react'
import DOMPurify from 'dompurify'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'

interface Announcement {
  id: string
  title: string
  content: string
  start_date: string
  end_date: string
  priority: number
}

export default function AnnouncementModal() {
  const { user } = useAuth()
  const [announcement, setAnnouncement] = useState<Announcement | null>(null)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    if (!user) return
    loadUnreadAnnouncement()
  }, [user])

  const loadUnreadAnnouncement = async () => {
    if (!user) return

    try {
      // 現在表示すべきお知らせを取得（未読のもの）
      const { data: announcements, error: fetchError } = await supabase
        .from('announcements')
        .select('*')
        .eq('is_active', true)
        .lte('start_date', new Date().toISOString())
        .gte('end_date', new Date().toISOString())
        .order('priority', { ascending: false })
        .order('created_at', { ascending: false })

      if (fetchError) throw fetchError
      if (!announcements || announcements.length === 0) return

      // 未読のお知らせをフィルタリング
      const { data: viewedIds, error: viewsError } = await supabase
        .from('announcement_views')
        .select('announcement_id')
        .eq('user_id', user.id)

      if (viewsError) throw viewsError

      const viewedSet = new Set(viewedIds?.map(v => v.announcement_id) || [])
      const unreadAnnouncement = announcements.find(a => !viewedSet.has(a.id))

      if (unreadAnnouncement) {
        setAnnouncement(unreadAnnouncement)
        setIsVisible(true)
      }
    } catch (error) {
      console.error('Error loading announcements:', error)
    }
  }

  const handleClose = async () => {
    if (!announcement || !user) return

    try {
      // 閲覧履歴を記録
      await supabase
        .from('announcement_views')
        .insert({
          user_id: user.id,
          announcement_id: announcement.id
        })

      setIsVisible(false)
      setAnnouncement(null)
    } catch (error) {
      console.error('Error marking announcement as read:', error)
      // エラーが発生してもモーダルは閉じる
      setIsVisible(false)
      setAnnouncement(null)
    }
  }

  if (!isVisible || !announcement) return null

  return createPortal(
    <div className="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center p-4 animate-fade-slide-in">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-2xl animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b bg-gradient-to-r from-indigo-500 to-purple-600">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <Bell className="h-6 w-6 text-white" />
            </div>
            <h2 className="text-lg sm:text-xl font-bold text-white">{announcement.title}</h2>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors text-white tap-highlight"
            aria-label="閉じる"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          <div className="prose prose-sm sm:prose max-w-none">
            <div
              className="whitespace-pre-wrap text-gray-700"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(announcement.content, {
                  ALLOWED_TAGS: ['b', 'i', 'u', 'strong', 'em', 'br', 'p', 'a', 'ul', 'ol', 'li'],
                  ALLOWED_ATTR: ['href', 'target', 'rel']
                })
              }}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 sm:p-6 border-t bg-gray-50">
          <button
            onClick={handleClose}
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium tap-highlight"
          >
            閉じる
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}
